import React, { useLayoutEffect, useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import NavBar from '../components/NavBar/Navigation';
import Footer from '../components/Footer/Footer';
import ValuesBanner from '../assets/ourvalues.png';
import TreeImage from '../assets/TreeValues.png';
import RootsImage from '../assets/RootsValues.png';
import GrassImage from '../assets/GrassValues.png';
import EmployeeHandbook from '../assets/coming-soon.png';
//import EmployeeHandbook from '../assets/EmployeeHandbook.pdf';
import './OurValues.css';

// The width the whole scene was designed/positioned at (your monitor).
// Everything inside .ov-canvas is scaled uniformly from this width down
// to whatever the real viewport is, so relative positions never drift.
const DESIGN_WIDTH = 1920;

// Below this width we stop scaling the 1920px canvas and switch to a
// dedicated stacked mobile layout instead. Scaling the canvas down that
// far makes the text/hit-targets too small to read/tap, so mobile gets
// its own normal-flow markup further down this file.
const MOBILE_BREAKPOINT = 768;

const ROOT_LETTERS = [
  { id: 'c', letter: 'C', left: '13%', top: '42%', variant: 'caring' },
  { id: 'a', letter: 'A', left: '18%', top: '78%', variant: 'agile' },
  { id: 'r', letter: 'R', left: '44%', top: '84%', variant: 'responsible' },
  { id: 'b', letter: 'B', left: '67%', top: '67%', variant: 'bold' },
  { id: 's', letter: 'S', left: '80%', top: '46%', variant: 'simple' },
];

const VALUES = [
  {
    id: 'caring',
    name: 'Caring',
    description:
      'We look out for people and outcomes by fostering a supportive, inclusive environment where individuals feel valued and heard. By caring for both our teams and the impact of our work, we build strong relationships and deliver outcomes that are sustainable, ethical, and meaningful.',
  },
  {
    id: 'agile',
    name: 'Agile',
    description:
      'We adapt our portfolio and respond proactively to change. By staying flexible and forward-looking, we are able to anticipate challenges, embrace new opportunities, and continuously adjust our approach to deliver the best possible outcomes.',
  },
  {
    id: 'responsible',
    name: 'Responsible',
    description:
      "We take ownership and deliver on our commitments. Responsibility means being dependable, following through on what we promise, and ensuring our work meets high standards while supporting Project Green's mission and long-term success.",
  },
  {
    id: 'bold',
    name: 'Bold',
    description:
      'We take ownership and are accountable for our decisions and actions. Boldness encourages us to speak up, challenge assumptions, and act with confidence - driving innovation, continuous improvement, and positive change.',
  },
  {
    id: 'simple',
    name: 'Simple',
    description:
      'We make planning and reporting clear and transparent. By keeping processes straightforward and communication open, we enable better decision-making, stronger collaboration, and a shared understanding of priorities and progress.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const popIn = {
  hidden: { opacity: 0, scale: 0.5 },
  show: (i) => ({
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.35,
      delay: 0.12 + i * 0.08,
      ease: 'easeOut',
    },
  }),
};

const cardVariants = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      delay: i * 0.08,
      ease: 'easeOut',
    },
  }),
};

// Small helper hook: tracks whether we're under the mobile breakpoint via
// matchMedia (so it responds to resizes/orientation changes, not just
// initial mount).
function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handleChange = (e) => setIsMobile(e.matches);

    setIsMobile(mql.matches);

    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }
    // Safari < 14 fallback
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, [breakpoint]);

  return isMobile;
}

const OurValues = () => {
  const outerRef = useRef(null);
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [canvasHeight, setCanvasHeight] = useState(0);
  const isMobile = useIsMobile();

  const recalc = useCallback(() => {
    const outerEl = outerRef.current;
    const canvasEl = canvasRef.current;
    if (!outerEl || !canvasEl) return;

    const outerWidth = outerEl.offsetWidth;
    // offsetHeight is the natural, un-transformed height of the canvas —
    // CSS transform: scale() never affects layout box measurements.
    const naturalHeight = canvasEl.offsetHeight;

    const nextScale = outerWidth / DESIGN_WIDTH;
    setScale(nextScale);
    setCanvasHeight(naturalHeight * nextScale);
  }, []);

  useLayoutEffect(() => {
    // The scaled canvas only exists on desktop — skip all of this work
    // (and the ResizeObserver) when we're rendering the mobile layout.
    if (isMobile) return;

    recalc();

    const outerEl = outerRef.current;
    const canvasEl = canvasRef.current;

    if (!outerEl || !canvasEl || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recalc);
      return () => window.removeEventListener('resize', recalc);
    }

    // Watch BOTH boxes:
    // - outerEl: its width changes drive the scale factor.
    // - canvasEl: its *natural* content height can change independently
    //   (e.g. the tree/roots images finishing their async load after
    //   first paint). If we only watch outerEl, canvasHeight goes stale
    //   the moment content grows post-mount, and the green background
    //   (which is sized off that stale height) ends too early — the
    //   last row of content then overflows into the footer below.
    const resizeObserver = new ResizeObserver(recalc);
    resizeObserver.observe(outerEl);
    resizeObserver.observe(canvasEl);

    return () => resizeObserver.disconnect();
  }, [recalc, isMobile]);

  return (
    <div className="ov-page">
      <NavBar />

      <section
        className="ov-banner"
        style={{ backgroundImage: `url(${ValuesBanner})` }}
      >
        <div className="ov-banner-overlay" />
        <div className="ov-banner-text">
          <h1 className="ov-banner-heading">Our Values</h1>
          <p className="ov-banner-sub">What we stand for</p>
        </div>
      </section>

      {isMobile ? (
        <section className="ov-m-green">
          {/* HERO */}
          <div className="ov-m-hero">
            <motion.h2
              className="ov-m-hero-title"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
            >
              CARBS
            </motion.h2>

            <motion.div
              className="ov-m-carbs-copy"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.35 }}
              variants={fadeUp}
            >
              <p className="ov-m-carbs-desc">
                When we talk about <strong>CARBS</strong>, we're not talking about food! - we're talking about how we work.
                <br />
                <br />
                <strong>CARBS</strong> are our shared values that keep us
                focused on clarity, accountability, and care for both
                people and results.
              </p>
            </motion.div>

            <motion.div
              className="ov-m-tree-wrap"
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              <img
                src={TreeImage}
                alt="Project Green values tree"
                className="ov-m-tree-img"
              />
            </motion.div>
          </div>

          {/* Grass overlaps the tree's bottom whitespace and the roots
              overlap the grass — same "one continuous illustration" idea
              as the desktop canvas, just tuned with margins instead of
              absolute px offsets. If your Tree/Roots PNGs have more or
              less transparent padding than assumed here, nudge the
              margin-top values in .ov-m-grass-line / .ov-m-roots-section
              in OurValues.css up or down until the trunk meets the roots. */}
          <div
            className="ov-m-grass-line"
            style={{ backgroundImage: `url(${GrassImage})` }}
            aria-hidden="true"
          />

          {/* ROOTS */}
          <div className="ov-m-roots-section">
            <div className="ov-m-roots-wrap">
              <motion.img
                src={RootsImage}
                alt="Roots representing CARBS values"
                className="ov-m-roots-img"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              />

              {ROOT_LETTERS.map((node, i) => (
                <motion.span
                  key={node.id}
                  className={`ov-m-letter ov-letter-${node.variant}`}
                  style={{ left: node.left, top: node.top }}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={popIn}
                  custom={i}
                >
                  {node.letter}
                </motion.span>
              ))}
            </div>
          </div>

          <motion.div
            className="ov-m-divider"
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <span className="ov-divider-dot" />
            <span className="ov-divider-track" />
            <span className="ov-divider-dot" />
          </motion.div>

          {/* VALUES — stacked single column on mobile instead of the
              crossword-style grid, which doesn't fit narrow screens. */}
          <div className="ov-m-values-list">
            {VALUES.map((value, i) => (
              <motion.div
                key={value.id}
                className="ov-m-value-card"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                variants={cardVariants}
                custom={i}
              >
                <span className={`ov-pill ov-pill-${value.id}`}>
                  {value.name}
                </span>
                <p className="ov-m-value-desc">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </section>
      ) : (
        <section className="ov-green">
          <div
            className="ov-canvas-outer"
            ref={outerRef}
            style={{ height: canvasHeight || undefined }}
          >
            <div
              className="ov-canvas"
              ref={canvasRef}
              style={{ transform: `scale(${scale})` }}
            >
              <div className="ov-hero-scene">
                <motion.h2
                  className="ov-hero-title"
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeUp}
                >
                  CARBS
                </motion.h2>

                <motion.div
                  className="ov-tree-wrap"
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                  <img
                    src={TreeImage}
                    alt="Project Green values tree"
                    className="ov-tree-img"
                    onLoad={recalc}
                  />
                </motion.div>

                <motion.div
                  className="ov-carbs-copy"
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.35 }}
                  variants={fadeUp}
                >
                  <p className="ov-carbs-desc">
                    When we talk about <strong>CARBS</strong>, we're not talking about food! - we're talking about how we work.<br></br>
                    <br></br>
                    <strong>CARBS</strong> are our shared values that keep us
                    focused on clarity, accountability, and care for both
                    people and results.
                  </p>
                </motion.div>

                <div
                  className="ov-grass-line"
                  style={{ backgroundImage: `url(${GrassImage})` }}
                  aria-hidden="true"
                />
              </div>

              <div className="ov-content-shell">
                <div className="ov-roots-section">
                  <div className="ov-roots-wrap">
                    <motion.img
                      src={RootsImage}
                      alt="Roots representing CARBS values"
                      className="ov-roots-img"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                      onLoad={recalc}
                    />

                    {ROOT_LETTERS.map((node, i) => (
                      <motion.span
                        key={node.id}
                        className={`ov-letter ov-letter-${node.variant}`}
                        style={{ left: node.left, top: node.top }}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.2 }}
                        variants={popIn}
                        custom={i}
                      >
                        {node.letter}
                      </motion.span>
                    ))}
                  </div>
                </div>

                <motion.div
                  className="ov-divider"
                  initial={{ opacity: 0, scaleX: 0 }}
                  whileInView={{ opacity: 1, scaleX: 1 }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                  <span className="ov-divider-dot" />
                  <span className="ov-divider-track" />
                  <span className="ov-divider-dot" />
                </motion.div>

                <div className="ov-values-grid">
                  {VALUES.map((value, i) => (
                    <motion.div
                      key={value.id}
                      className={`ov-value-card ov-value-${value.id}`}
                      initial="hidden"
                      whileInView="show"
                      viewport={{ once: true, amount: 0.2 }}
                      variants={cardVariants}
                      custom={i}
                    >
                      <span className={`ov-pill ov-pill-${value.id}`}>
                        {value.name}
                      </span>
                      <p className="ov-value-desc">{value.description}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <motion.section
        className="ov-handbook"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <p className="ov-handbook-eyebrow">Want to know more about Project Green?</p>
        <h3 className="ov-handbook-title">Employee Handbook coming soon</h3>
        {/* <div className="ov-handbook-actions">
          <a
            href={EmployeeHandbook}
            target="_blank"
            rel="noopener noreferrer"
            className="ov-handbook-btn ov-handbook-btn-view"
          >
            View
          </a>

           Download is disabled until the handbook PDF is ready.
              To re-enable: replace this block with
              <a href={EmployeeHandbook} download="Employee Handbook.pdf"
                 className="ov-handbook-btn ov-handbook-btn-download">Download</a> 
          <div className="ov-handbook-download-wrap">
            <button
              type="button"
              className="ov-handbook-btn ov-handbook-btn-download is-disabled"
              disabled
              aria-disabled="true"
            >
              Download
            </button>
            <span className="ov-handbook-ribbon" aria-hidden="true">
              Coming soon
            </span>
          </div>
        </div> */}
      </motion.section>

      <Footer />
    </div>
  );
};

export default OurValues;